import styled from '@emotion/styled';
import type { ReactNode } from 'react';

import { useSnowplow } from 'ms-helpers/Snowplow/useSnowplow';
import { FreeTextbooksBookDemoButton } from 'ms-pages/FreeTextbooks/FreeTextbooksBookDemoButton';
import Separator from 'ms-pages/Teacher/components/Separator';
import { font, fontFamily, fontSize } from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import { BASE_UNIT } from 'ms-styles/theme/Numero';
import AnchorButton from 'ms-ui-primitives/Button/AnchorButton';
import { signupSunflowerUrl } from 'ms-utils/urls';

const Wrapper = styled.div({
  height: 400,
  overflow: 'hidden',
  position: 'relative',
  color: colors.regentGray,
});

const Banner = styled.div({
  position: 'absolute',
  bottom: 0,
  right: 0,
  left: 0,
  top: 100,
  color: colors.cloudBurst,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  background:
    'linear-gradient(0deg, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 70%, rgba(255,255,255,0) 100%);',
  zIndex: 1, // A temporary fix for `CartesianPlane`
});

const BannerText = styled.div({
  fontFamily: fontFamily.body,
  fontSize: fontSize.medium,
  color: colors.regentGray,
  lineHeight: '22px',
  maxWidth: 450,
  textAlign: 'center',
});

const BannerHeading = styled.div({
  ...font.gilroyLargeHeading,
  color: colors.neutralGray,
});

const CTAWrapper = styled.div({
  display: 'flex',
});

type Props = {
  title: string;
  description?: string | undefined;
  snowplowEventLabel: string;
  children: ReactNode;
};

export function FreeTextbooksTeaserContent({
  title,
  description = 'Get full access to our content with a Mathspace account',
  snowplowEventLabel,
  children,
}: Props) {
  const { trackStructEvent } = useSnowplow();

  return (
    <Wrapper>
      {children}
      <Banner>
        <Charactero />
        <Separator size={3 * BASE_UNIT} />
        <BannerHeading>{title}</BannerHeading>
        <Separator size={3 * BASE_UNIT} />
        <BannerText>{description}</BannerText>
        <Separator size={5 * BASE_UNIT} />
        <CTAWrapper>
          <FreeTextbooksBookDemoButton
            type="secondary"
            size="large"
            isRound
            trackingId="FreeTextbook/BookDemo"
            snowplowEventLabel={snowplowEventLabel}
          />
          <Separator size={5 * BASE_UNIT} />
          <AnchorButton
            href={signupSunflowerUrl}
            target="_blank"
            type="primary"
            size="large"
            isRound
            trackingId="FreeTextbook/SignUp"
            onClick={() => {
              trackStructEvent({
                category: 'free_textbook',
                action: 'clicked_sunflower_free_trial',
                label: snowplowEventLabel,
              });
            }}
          >
            Sign up for free
          </AnchorButton>
        </CTAWrapper>
      </Banner>
    </Wrapper>
  );
}

const Charactero = () => (
  <svg
    width="140"
    height="162"
    viewBox="0 0 140 162"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.94533 53.6696C7.54695 52.6028 9.71385 53.0466 10.793 54.6511C10.8701 54.7706 12.2062 56.6994 14.4502 58.9355C15.5722 60.0536 16.9254 61.2484 18.4414 62.3409C19.9573 63.4333 21.6446 64.4148 23.4432 65.1317C25.1133 65.7974 26.8777 66.2412 28.7277 66.3607C30.6548 66.4887 32.1194 68.1445 31.9994 70.0648C31.8795 71.9851 30.2265 73.4445 28.2995 73.3165C25.6187 73.1458 23.1263 72.4971 20.8652 71.601C18.3557 70.5939 16.1289 69.271 14.1932 67.8714C11.2898 65.7633 9.02866 63.4675 7.46987 61.6752C5.91107 59.8829 5.04603 58.6027 4.97751 58.5088C3.91548 56.8957 4.34372 54.7365 5.94533 53.6696Z"
      fill="#C17F50"
    />
    <path
      d="M11.1506 61.4998C14.3347 59.633 15.3914 55.5376 13.5108 52.3525C11.6303 49.1674 7.52453 48.0987 4.34042 49.9655C1.15631 51.8322 0.0995791 55.9276 1.98015 59.1127C3.86072 62.2978 7.96646 63.3665 11.1506 61.4998Z"
      fill="#C17F50"
    />
    <path
      d="M133.198 59.1506C131.596 58.0838 129.429 58.5276 128.35 60.1321C128.273 60.2516 126.937 62.1804 124.693 64.4165C123.571 65.5346 122.218 66.7294 120.702 67.8218C119.186 68.9143 117.498 69.8958 115.7 70.6127C114.03 71.2784 112.265 71.7222 110.415 71.8417C108.488 71.9697 107.024 73.6254 107.144 75.5458C107.264 77.4661 108.917 78.9255 110.844 78.7975C113.524 78.6268 116.017 77.9781 118.278 77.082C120.787 76.0749 123.014 74.752 124.95 73.3523C127.853 71.2443 130.114 68.9484 131.673 67.1561C133.232 65.3639 134.097 64.0837 134.166 63.9898C135.228 62.3767 134.799 60.2174 133.198 59.1506Z"
      fill="#C17F50"
    />
    <path
      d="M127.993 66.9806C124.809 65.1138 123.752 61.0184 125.632 57.8333C127.513 54.6482 131.619 53.5795 134.803 55.4463C137.987 57.3131 139.044 61.4085 137.163 64.5936C135.282 67.7787 131.177 68.8474 127.993 66.9806Z"
      fill="#C17F50"
    />
    <path
      d="M85.1714 126.106L85.1029 147.042C85.1029 147.042 98.9008 144.498 97.145 151.548C95.5862 157.795 77.6258 157.992 76.8464 153.34C75.5703 145.77 76.3326 127.301 76.3326 127.301L85.1714 126.106Z"
      fill="#C17F50"
    />
    <path
      d="M59.2973 126.217L58.5007 147.195C58.5007 147.195 44.8485 145.446 46.3131 152.291C47.6149 158.359 65.541 157.249 66.5174 152.478C68.1104 144.729 68.1104 126.302 68.1104 126.302L59.2973 126.217Z"
      fill="#C17F50"
    />
    <path
      d="M108.11 120.649C117.965 121.849 136.341 115.33 137.118 98.9751C137.243 96.2794 133.722 96.0901 132.299 98.3869C129.418 103.017 122.394 112.106 110.169 110.524C90.7748 108.014 95.6184 119.127 108.11 120.649Z"
      fill="#C17F50"
    />
    <path
      d="M46.7885 11.0913C45.4253 10.5976 38.9976 7.46309 31.1901 10.8527C23.3743 14.2505 19.5739 32.8685 26.0099 42.2639C32.4541 51.6675 42.4428 25.0774 43.5498 22.0992C44.6569 19.121 48.9779 11.8811 46.7885 11.0913Z"
      fill="#C17F50"
    />
    <path
      d="M98.152 13.0052C88.3177 3.20152 49.5671 2.37739 39.4654 13.7349C37.3605 16.1043 34.0565 19.3579 32.3744 24.1825C30.2177 30.3635 29.0617 48.1595 29.5103 48.2368H29.4931C29.2084 64.2729 29.8813 116.949 40.0606 126.555C49.8691 135.818 90.7332 137.148 103.155 126.958C119.796 113.309 106.183 21.0147 98.152 13.0052Z"
      fill="#E3A263"
    />
    <path
      d="M28.036 48.0822C28.4587 48.168 28.8987 48.2539 29.3472 48.3225H29.33C29.33 48.3311 29.33 48.3483 29.33 48.3569H45.1943V24.6718C45.1943 24.6718 13.6641 25.0838 13.8884 35.4799C13.9402 38.184 15.5706 45.4724 28.036 48.0822Z"
      fill="#E3A263"
    />
    <path
      d="M40.2767 34.8618C40.3198 38.0038 35.118 44.0903 28.6222 44.1676C22.1177 44.2448 16.7779 38.2957 16.7348 35.1537C16.6916 32.0117 21.9366 29.7796 28.441 29.7024C34.9455 29.6251 40.2422 31.7198 40.2767 34.8618Z"
      fill="#3B1C0C"
    />
    <path
      d="M28.6828 32.2264C28.6914 33.0677 27.199 33.7631 25.3356 33.7889C23.4809 33.8146 21.9626 33.1536 21.954 32.3123C21.9454 31.471 23.4378 30.7756 25.3011 30.7499C27.1645 30.7241 28.6741 31.3851 28.6828 32.2264Z"
      fill="#6C442F"
    />
    <path
      d="M91.9446 15.8936C93.0635 14.9748 98.0678 9.88158 106.565 10.4777C115.072 11.0788 124.911 27.3601 122.003 38.3565C119.089 49.3633 100.74 27.6283 98.6961 25.19C96.6519 22.7516 90.1467 17.3666 91.9446 15.8936Z"
      fill="#C17F50"
    />
    <path
      d="M30.3574 50.7637C30.409 50.7894 32.0071 51.5675 34.5505 52.3541C37.0938 53.1322 40.5994 53.9189 44.4832 53.9189C46.236 53.9189 48.0661 53.7564 49.9135 53.3545C51.2968 53.0553 52.6974 52.6192 54.0807 52.0206C56.1515 51.1228 58.1793 49.8402 60.0696 48.1045C61.9599 46.3602 63.6955 44.1627 65.2078 41.4265C65.603 40.7168 65.3366 39.8275 64.6235 39.4342C63.9103 39.0409 63.0167 39.306 62.6215 40.0157C61.4014 42.2302 60.0438 44.0088 58.6003 45.4367C57.5177 46.5141 56.3921 47.3862 55.2407 48.1045C53.5051 49.1819 51.7007 49.9001 49.8877 50.3533C48.0747 50.8065 46.2446 50.986 44.4832 50.986C41.0033 50.986 37.7726 50.2678 35.4269 49.5495C34.2497 49.1904 33.3046 48.8313 32.6516 48.5662C32.3251 48.4294 32.0759 48.3182 31.9126 48.2498C31.8267 48.2156 31.7666 48.1814 31.7322 48.1643C31.715 48.1558 31.6978 48.1472 31.6892 48.1472H31.6806L31.4658 48.5748L31.6806 48.1472L31.4658 48.5748L31.6806 48.1472L31.2854 48.951L31.6806 48.1472L31.2854 48.951L31.6806 48.1472L31.0362 49.4384L31.6806 48.1472L31.0362 49.4384L31.6806 48.1472C30.9503 47.7881 30.0653 48.0874 29.7044 48.8142C29.3264 49.5239 29.6271 50.4046 30.3574 50.7637Z"
      fill="#C17F50"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M32.6887 16.7398C33.2587 16.2345 34.1324 16.2846 34.6402 16.8518L39.6637 22.4631C40.0057 22.8451 40.1063 23.3845 39.925 23.8632C39.7436 24.342 39.3102 24.681 38.7999 24.7433L30.1796 25.7957C29.4219 25.8882 28.7323 25.352 28.6394 24.598C28.5464 23.844 29.0853 23.1578 29.843 23.0653L35.8445 22.3326L32.5761 18.6818C32.0683 18.1146 32.1188 17.2451 32.6887 16.7398Z"
      fill="#3B1C0C"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M68.5544 18.2767C68.1923 17.608 67.354 17.3579 66.682 17.7182L57.356 22.7186C56.9034 22.9612 56.6235 23.4339 56.6295 23.9455C56.6355 24.4571 56.9263 24.9231 57.3845 25.1552L68.2415 30.6558C68.9217 31.0005 69.754 30.7311 70.1003 30.0541C70.4466 29.3771 70.1759 28.5489 69.4957 28.2043L60.9899 23.895L67.9932 20.14C68.6652 19.7797 68.9165 18.9455 68.5544 18.2767Z"
      fill="#3B1C0C"
    />
    <path
      d="M15.826 46.7179C13.8538 43.1169 11.0673 51.4842 7.88232 54.6817L14.775 60.0555C16.1446 56.8239 17.7983 50.319 15.826 46.7179Z"
      fill="#C17F50"
    />
    <path
      d="M121.433 54.6346C118.276 55.3941 124.108 59.2112 125.904 62.2759L131.42 58.151C129.228 56.4544 124.59 53.8751 121.433 54.6346Z"
      fill="#C17F50"
    />
  </svg>
);
